import {
  addDoc,
  collection,
  doc,
  getDoc,
  runTransaction,
  updateDoc
} from 'firebase/firestore';

import { status } from '../../../../constants/enum';
import { throwError } from '../../../../modules/error';
import { deleteFile, uploadFile } from '../../../aws/s3';
import { db } from '../../config';
import { usersRef } from '../user';

import type { MediaType } from './mediaType';
import type {
  CollectionReference,
  DocumentData,
  DocumentReference,
  DocumentSnapshot
} from 'firebase/firestore';

export const mediasRef = collection(db, 'medias');

export const mediaDocument = (mediaId: string): DocumentReference =>
  doc(mediasRef, mediaId);

export const mediaSnapshot = async (
  mediaId: string
): Promise<DocumentSnapshot> => await getDoc(mediaDocument(mediaId));

export const updateDraftFlgFromMicroCms = async (
  mediaId: string | number
): Promise<void> => {
  // 更新するメディア情報を取得
  if (typeof mediaId === 'string') {
    const mediaDoc = doc(mediasRef, mediaId);
    const mediaData = {
      draftFlg: false,
      updated: new Date()
    };
    // メディア情報の記事IDを更新
    await updateDoc(mediaDoc, mediaData);
  } else {
    throwError('media is not string');
  }
};

export const updateArticleInfo = async (
  mediaId: string | number,
  articleId: string,
  draftFlg: boolean
): Promise<void> => {
  // 更新するメディア情報を取得
  if (typeof mediaId === 'string') {
    const mediaDoc = doc(mediasRef, mediaId);
    const mediaData = {
      articleId,
      draftFlg,
      fromMediage: true,
      updated: new Date()
    };
    // メディア情報の記事IDを更新
    await updateDoc(mediaDoc, mediaData);
  } else {
    throwError('media is not string');
  }
};

export const getMedias = async (mediaId: string): Promise<MediaType> => {
  const mediaRef = doc(mediasRef, mediaId);
  const mediaSnap = await getDoc(mediaRef);
  return mediaSnap.data() as MediaType;
};

export const favoriteMediasRef = (
  mediaId: string
): CollectionReference<DocumentData> =>
  collection(db, 'medias', mediaId, 'favoriteMedia');

export const addFavoriteMedia = async (
  uid: string,
  mediaId: string,
  companyId: string
): Promise<void> => {
  try {
    await runTransaction(db, async (transaction) => {
      const mediaRef = doc(db, 'medias', mediaId);
      const mediaSnap = await transaction.get(mediaRef);
      const mediaCompanyId = mediaSnap.data()?.companyId as string;

      const favoriteData = {
        companyId,
        created: new Date(),
        mediaCompanyId,
        mediaId,
        uid,
        updated: new Date()
      };

      const favoriteMediaRef = collection(
        db,
        'medias',
        mediaId,
        'favoriteMedia'
      );
      const favoriteDoc = await addDoc(favoriteMediaRef, favoriteData);
      await updateDoc(
        doc(db, 'medias', mediaId, 'favoriteMedia', favoriteDoc.id),
        {
          favoriteMediaId: favoriteDoc.id
        }
      );

      const countRef = doc(db, 'medias', mediaId);
      const countSnap = await transaction.get(countRef);
      const currentCount = (countSnap.data()?.favoriteCount as number) || 0;
      const newCount = currentCount + 1;
      transaction.update(countRef, { favoriteCount: newCount });
    });
  } catch (e) {
    throwError(e);
  }
};

export const deleteFavorite = async (
  mediaId: string,
  mediaFavoriteId: string
): Promise<void> => {
  try {
    await runTransaction(db, async (transaction) => {
      const path = favoriteMediasRef(mediaId);
      const favoriteRef = doc(path, mediaFavoriteId);

      const countRef = doc(db, 'medias', mediaId);
      const countSnap = await transaction.get(countRef);
      const currentCount = (countSnap.data()?.favoriteCount as number) || 0;
      const newCount = currentCount - 1;
      transaction.update(countRef, { favoriteCount: newCount });

      transaction.delete(favoriteRef);
    });
  } catch (e) {
    throwError(e);
  }
};

const checkboxArray = (value: object[]): number[] => {
  const array: number[] = [];
  value.forEach((elm: object, key) => {
    if (elm) {
      array.push(key);
    }
  });
  return array;
};

export const addMedia = async (
  uid: string,
  watch: (arg0: string) => any,
  fileLogo: FileList | null
): Promise<string> => {
  const userRef = doc(usersRef, uid);
  const userSnap = await getDoc(userRef);
  if (userSnap.exists()) {
    const companyId = userSnap.data()?.companyId as string;
    const mediaData = {
      assigneeId: uid,
      category: checkboxArray(watch('genre') as object[]),
      categoryOther: (watch('genreOther') as string) || null,
      companyId,
      created: new Date(),
      funnel: checkboxArray(watch('funnel') as object[]),
      mediaType: Number(watch('mediaType')),
      name: watch('mediaName') as string,
      overview: watch('overview') as string,
      scaleEmailSubscribers: watch('scaleEmailSubscribers')
        ? Number(String(watch('scaleEmailSubscribers')).replace(/,/g, ''))
        : null,
      scaleMembers: watch('scaleMembers')
        ? Number(String(watch('scaleMembers')).replace(/,/g, ''))
        : null,
      scalePvMau: watch('scalePvMau')
        ? Number(String(watch('scalePvMau')).replace(/,/g, ''))
        : null,
      scaleSnsFollowers: {
        Facebook: watch('facebook')
          ? Number(String(watch('facebook')).replace(/,/g, ''))
          : null,
        Instagram: watch('instagram')
          ? Number(String(watch('instagram')).replace(/,/g, ''))
          : null,
        Line: watch('line')
          ? Number(String(watch('line')).replace(/,/g, ''))
          : null,
        TikTok: watch('tiktok')
          ? Number(String(watch('tiktok')).replace(/,/g, ''))
          : null,
        Twitter: watch('twitter')
          ? Number(String(watch('twitter')).replace(/,/g, ''))
          : null,
        Youtube: watch('youtube')
          ? Number(String(watch('youtube')).replace(/,/g, ''))
          : null,
        note: watch('note')
          ? Number(String(watch('note')).replace(/,/g, ''))
          : null
      },
      scaleUu: watch('scaleUu')
        ? Number(String(watch('scaleUu')).replace(/,/g, ''))
        : null,
      status: status.notChecked,
      updated: new Date()
    };
    const mediaRef = await addDoc(mediasRef, mediaData);
    const fileNameLogo = fileLogo !== null ? fileLogo[0].name : '';
    const logoPath =
      fileNameLogo !== ''
        ? `medias/${mediaRef.id}/image/${Date.now()}_${fileNameLogo}`
        : null;
    if (logoPath !== null) {
      uploadFile(fileLogo, logoPath);
      await updateDoc(mediaRef, { image: logoPath });
    }
    await updateDoc(mediaRef, { mediaId: mediaRef.id });
    return mediaRef.id;
  }
  return '';
};

export const editMedia = async (
  watch: (arg0: string) => any,
  mediaId: string,
  fileLogo: FileList | null
): Promise<void> => {
  const mediaRef = doc(mediasRef, mediaId);
  const mediaSnap = await getDoc(mediaRef);
  const oldLogo = mediaSnap.data()?.image as string;
  const fileNameLogo = fileLogo !== null ? fileLogo[0].name : '';
  const logoPath =
    fileNameLogo !== ''
      ? `medias/${mediaRef.id}/image/${Date.now()}_${fileNameLogo}`
      : null;
  const mediaData = {
    category: checkboxArray(watch('genre') as object[]),
    categoryOther: (watch('genreOther') as string) || null,
    funnel: checkboxArray(watch('funnel') as object[]),
    mediaType: Number(watch('mediaType')),
    name: watch('mediaName') as string,
    overview: watch('overview') as string,
    scaleEmailSubscribers: watch('scaleEmailSubscribers')
      ? Number(String(watch('scaleEmailSubscribers')).replace(/,/g, ''))
      : null,
    scaleMembers: watch('scaleMembers')
      ? Number(String(watch('scaleMembers')).replace(/,/g, ''))
      : null,
    scalePvMau: watch('scalePvMau')
      ? Number(String(watch('scalePvMau')).replace(/,/g, ''))
      : null,
    scaleSnsFollowers: {
      Facebook: watch('facebook')
        ? Number(String(watch('facebook')).replace(/,/g, ''))
        : null,
      Instagram: watch('instagram')
        ? Number(String(watch('instagram')).replace(/,/g, ''))
        : null,
      Line: watch('line')
        ? Number(String(watch('line')).replace(/,/g, ''))
        : null,
      TikTok: watch('tiktok')
        ? Number(String(watch('tiktok')).replace(/,/g, ''))
        : null,
      Twitter: watch('twitter')
        ? Number(String(watch('twitter')).replace(/,/g, ''))
        : null,
      Youtube: watch('youtube')
        ? Number(String(watch('youtube')).replace(/,/g, ''))
        : null,
      note: watch('note')
        ? Number(String(watch('note')).replace(/,/g, ''))
        : null
    },
    scaleUu: watch('scaleUu')
      ? Number(String(watch('scaleUu')).replace(/,/g, ''))
      : null,
    updated: new Date()
  };

  // ロゴをs3に登録
  if (logoPath !== null) {
    uploadFile(fileLogo, logoPath);
    await updateDoc(mediaRef, { image: logoPath });
  }

  // 古いロゴは削除
  if (logoPath !== null && oldLogo) {
    deleteFile(oldLogo);
  }

  const mediaDoc = doc(mediasRef, mediaId);
  await updateDoc(mediaDoc, mediaData);
};

export const editMedia2 = async (
  watch: (arg0: string) => any,
  mediaId: string
): Promise<void> => {
  const mediaData = {
    demographicsArea: {
      chubu: watch('area4') as number,
      chugoku: watch('area6') as number,
      hokkaidou: watch('area1') as number,
      kantou: watch('area3') as number,
      kinki: watch('area5') as number,
      kyushu: watch('area8') as number,
      shikoku: watch('area7') as number,
      touhoku: watch('area2') as number
    },
    demographicsDevice: {
      pc: watch('device1') as number,
      sp: watch('device2') as number,
      tablet: watch('device3') as number
    },
    demographicsGender: {
      female: watch('genderFemale') as number,
      male: watch('genderMale') as number
    },
    demographicsGeneration: {
      f10: watch('f10') as number,
      f20: watch('f20') as number,
      f30: watch('f30') as number,
      f40: watch('f40') as number,
      f50: watch('f50') as number,
      f60: watch('f60') as number,
      f70: watch('f70') as number,
      m10: watch('m10') as number,
      m20: watch('m20') as number,
      m30: watch('m30') as number,
      m40: watch('m40') as number,
      m50: watch('m50') as number,
      m60: watch('m60') as number,
      m70: watch('m70') as number
    },
    demographicsOther: watch('targetDetail') as string,
    target: checkboxArray(watch('target') as object[]),
    targetType: Number(watch('targetKind')),
    updated: new Date(),
    value: watch('merit') as string
  };
  const mediaDoc = doc(mediasRef, mediaId);
  await updateDoc(mediaDoc, mediaData);
};

export const editMedia3 = async (
  watch: (arg0: string) => any,
  mediaId: string,
  materialfiles: Array<FileList | string> = [],
  materialDeleteFiles: string[] = [],
  seasonalfiles: Array<FileList | string> = [],
  seasonalDeleteFiles: string[] = [],
  otherMaterialfiles: Array<FileList | string> = [],
  otherMaterialDeleteFiles: string[] = []
): Promise<void> => {
  const materialFileNames: Array<string | null> = [];
  const seasonalFileNames: Array<string | null> = [];
  const otherMaterialFileNames: Array<string | null> = [];

  materialfiles.map((file) => {
    if (typeof file === 'string') {
      materialFileNames.push(file);
      return materialFileNames;
    }
    const fileName = file !== null ? file[0].name : '';
    const image =
      fileName !== ''
        ? `medias/${mediaId}/file/${Date.now()}_${fileName}`
        : null;
    materialFileNames.push(image);
    return image !== null && uploadFile(file, image);
  });

  seasonalfiles.map((file) => {
    if (typeof file === 'string') {
      seasonalFileNames.push(file);
      return seasonalFileNames;
    }
    const fileName = file !== null ? file[0].name : '';
    const image =
      fileName !== ''
        ? `medias/${mediaId}/file/${Date.now()}_${fileName}`
        : null;
    seasonalFileNames.push(image);
    return image !== null && uploadFile(file, image);
  });

  otherMaterialfiles.map((file) => {
    if (typeof file === 'string') {
      otherMaterialFileNames.push(file);
      return otherMaterialFileNames;
    }
    const fileName = file !== null ? file[0].name : '';
    const image =
      fileName !== ''
        ? `medias/${mediaId}/file/${Date.now()}_${fileName}`
        : null;
    otherMaterialFileNames.push(image);
    return image !== null && uploadFile(file, image);
  });

  if (mediaId) {
    materialDeleteFiles.forEach((deleteFileName): void => {
      deleteFile(deleteFileName);
    });
    seasonalDeleteFiles.forEach((deleteFileName): void => {
      deleteFile(deleteFileName);
    });
    otherMaterialDeleteFiles.forEach((deleteFileName): void => {
      deleteFile(deleteFileName);
    });
  }

  const mediaData = {
    adOption: (watch('adTypeOther') as string) || null,
    adType: checkboxArray(watch('adType') as object[]),
    budget: Number(watch('budget')),
    castingFlg: watch('compensation.4') as boolean,
    dueDate: (watch('dueDate') as string) ?? null,
    editorFlg: watch('compensation.1') as boolean,
    guarantee: watch('compensation.2') as boolean,
    guaranteeFlg: watch('guaranteeFlg')
      ? checkboxArray(watch('guaranteeFlg') as object[])
      : [],
    holdingData: (watch('holdingData') as string) || null,
    holdingDataFlg: Number(watch('holdingDataFlg')) === 1,
    mediaMaterialFile: materialFileNames,
    mediaMaterialUpdateCycle: Number(watch('mediaMaterialUpdateCycle')),
    otherMaterialFile: otherMaterialFileNames,
    otherStrength: (watch('otherStrength') as string) || null,
    payment: checkboxArray(watch('payment') as object[]),
    paymentFlg: Number(watch('paymentFlg')) === 1,
    reportFlg: watch('compensation.3') as boolean,
    researchSchemeFlg: watch('compensation.6') as boolean,
    seasonalMenuMaterialFile: seasonalFileNames,
    seasonalMenuMaterialText:
      (watch('seasonalMenuMaterialText') as string) || null,
    secondaryUseFlg: watch('compensation.5') as boolean,
    similarMedia: [
      {
        comparison: (watch('similarMediaComparison1') as string) ?? null,
        mediaName: (watch('similarMediaName1') as string) ?? null
      },
      {
        comparison: (watch('similarMediaComparison2') as string) ?? null,
        mediaName: (watch('similarMediaName2') as string) ?? null
      }
    ],
    updated: new Date()
  };
  const mediaDoc = doc(mediasRef, mediaId);
  await updateDoc(mediaDoc, mediaData);
};

export const editMedia4 = async (
  watch: (arg0: string) => any,
  mediaId: string
): Promise<void> => {
  const mediaData = {
    clientsBenefits: watch('clientsBenefits') as string,
    continuousFactors: [
      watch('continuousFactors1') as string,
      watch('continuousFactors2') as string,
      watch('continuousFactors3') as string
    ],
    industries: [
      {
        industriesNum: Number(watch('industriesNum1')),
        industriesOther: (watch('industriesOther1') as string) ?? null,
        percentage: Number(watch('industriesPercentage1')) || null
      },
      {
        industriesNum: Number(watch('industriesNum2')),
        industriesOther: (watch('industriesOther2') as string) ?? null,
        percentage: Number(watch('industriesPercentage2')) || null
      },
      {
        industriesNum: Number(watch('industriesNum3')),
        industriesOther: (watch('industriesOther3') as string) ?? null,
        percentage: Number(watch('industriesPercentage3')) || null
      }
    ],
    mainKpi: checkboxArray(watch('mainKpi') as object[]),
    mainKpiOther: (watch('mainKpiOther') as string) ?? null,
    mediaMenuUrl: (watch('mediaMenuUrl') as string) ?? null,
    updated: new Date()
  };
  const mediaDoc = doc(mediasRef, mediaId);
  await updateDoc(mediaDoc, mediaData);
};
